import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { tkr, fmt } from '../utils';
import { getDailyUnits, getDailyHours, getBelaggning } from '../qlik';
import { ExportBtn } from '../components/SharedComponents';
import {
  AVDELNINGAR, beraknaNycklar, beraknaVeckor, fjolarsNiva, malForslag, skift, mandag,
} from './bemanningModel';

const FARG = '#1B2A4A';
const MANAD = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const dagMan = (d) => `${d.getDate()} ${MANAD[d.getMonth()]}`;
const h1 = (n) => (n == null ? '–' : `${(Math.round(n * 10) / 10).toLocaleString('sv-SE')} h`);

// Kommande måndag — planeringen görs för veckor som inte börjat
function kommandeMandag(idag = new Date()) {
  const m = mandag(idag);
  return skift(m, 7);
}

const chip = (aktiv) => ({
  fontSize: 11.5, fontWeight: 600, padding: '4px 11px', borderRadius: 999,
  border: '1px solid ' + (aktiv ? FARG : 'var(--border)'),
  background: aktiv ? FARG : 'transparent',
  color: aktiv ? '#fff' : 'var(--text-secondary)', cursor: 'pointer',
});

export default function BemanningTab({ isAdmin = false }) {
  const [units, setUnits]   = useState(null);
  const [hours, setHours]   = useState(null);
  const [bel, setBel]       = useState(null);
  const [mal, setMal]       = useState({});      // { avdId: 0.45 }
  const [planerat, setPlanerat] = useState({});  // { "2026-W34": { avdId: timmar } }
  const [laddat, setLaddat] = useState(false);
  const [startSel, setStartSel] = useState('');
  const [veckor, setVeckor] = useState(4);
  const [malOppen, setMalOppen] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    Promise.all([getDailyUnits(), getDailyHours(), getBelaggning()])
      .then(([u, h, b]) => { setUnits(u); setHours(h); setBel(b); })
      .catch((e) => console.error('Bemanning: datafel', e))
      .finally(() => setLaddat(true));
    getDoc(doc(db, 'intel_config', 'bemanning'))
      .then((s) => {
        const m = s.exists() ? (s.data().mal || {}) : {};
        setMal(m);
        setPlanerat(s.exists() ? (s.data().planerat || {}) : {});
        // Utan mål räknas inga tillåtna timmar fram, och kolumnerna står
        // tomma. Fäll då ut panelen direkt i stället för att gömma det enda
        // som gör vyn användbar bakom en klick.
        if (Object.keys(m).length === 0) setMalOppen(true);
      })
      .catch((e) => console.error('Bemanning: konfig kunde inte läsas', e));
  }, []);

  const start = useMemo(
    () => (startSel ? new Date(startSel + 'T12:00:00') : kommandeMandag()),
    [startSel]
  );

  const nycklar = useMemo(
    () => (units && hours ? beraknaNycklar(units, hours) : null),
    [units, hours]
  );

  const niva = useMemo(
    () => (units && hours && nycklar
      ? fjolarsNiva(units, hours, nycklar, mandag(start), skift(mandag(start), veckor * 7 - 1))
      : null),
    [units, hours, nycklar, start, veckor]
  );

  const rader = useMemo(
    () => (units && hours && bel && nycklar
      ? beraknaVeckor({ units, hours, bel, nycklar, mal, planerat, start, veckor })
      : []),
    [units, hours, bel, nycklar, mal, planerat, start, veckor]
  );

  const spara = async (nyttMal, nyttPlanerat) => {
    if (!isAdmin) return;
    setStatus('sparar');
    try {
      // INTE merge: Firestore djup-mergar nästlade map-fält, så ett borttaget
      // mål eller nollställt timtal skulle ligga kvar på servern. Dokumentet
      // innehåller bara dessa fyra fält — hela det skrivs om varje gång.
      await setDoc(doc(db, 'intel_config', 'bemanning'), {
        mal: nyttMal ?? mal,
        planerat: nyttPlanerat ?? planerat,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || '',
      });
      setStatus('sparat');
      setTimeout(() => setStatus(''), 1800);
    } catch (e) {
      console.error('Bemanning: kunde inte spara', e);
      setStatus('fel');
    }
  };

  const sattMal = (avdId, procent) => {
    const n = { ...mal };
    if (procent == null || isNaN(procent)) delete n[avdId];
    else n[avdId] = Math.min(2, Math.max(0.05, procent / 100));
    setMal(n);
    spara(n, null);
  };

  const sattPlan = (nyckel, avdId, timmar) => {
    const vecka = { ...(planerat[nyckel] || {}) };
    if (timmar == null || isNaN(timmar)) delete vecka[avdId];
    else vecka[avdId] = Math.max(0, timmar);
    const n = { ...planerat, [nyckel]: vecka };
    if (Object.keys(vecka).length === 0) delete n[nyckel];
    setPlanerat(n);
    spara(null, n);
  };

  if (!laddat) {
    return <div className="card"><p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Laddar bemanningsdata…</p></div>;
  }
  if (!nycklar) {
    return <div className="card"><p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Ingen data tillgänglig.</p></div>;
  }

  const saknasMal = AVDELNINGAR.filter(a => mal[a.id] == null);

  return (
    <div style={{ fontFamily: 'system-ui,sans-serif' }}>

      {/* ── NYCKELN ── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Bemanningsnyckel</div>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-secondary)' }}>
            <span>Från</span>
            <input type="date" value={startSel} onChange={(e) => setStartSel(e.target.value)} aria-label="Startdatum"
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px', fontSize: 11.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)' }} />
            {[2, 4, 8].map(v => (
              <button key={v} onClick={() => setVeckor(v)} style={chip(veckor === v)}>{v} v</button>
            ))}
            {(startSel || veckor !== 4) && (
              <button onClick={() => { setStartSel(''); setVeckor(4); }} style={chip(false)}>Nollställ</button>
            )}
          </div>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '6px 0 0' }}>
          Tillåtna timmar = (prognos × mål-personal% − fasta löner) ÷ rörlig kr per timme.
          Prognosen är fjolårets samma veckodag, justerad med bokningsläget.
        </p>
        {hours && !hours.harLon && (
          <p style={{ fontSize: 12, color: '#A32D2D', margin: '8px 0 0' }}>
            Lönekolumnerna saknas ännu i cachen — kr/timme räknas all-in (fast + rörligt) tills nästa
            hämtning gått igenom. Avdelningar med mycket fast lön ser då dyrare ut än de är.
          </p>
        )}
        {saknasMal.length > 0 && (
          <p style={{ fontSize: 12.5, color: '#8A6D1F', margin: '8px 0 0' }}>
            {saknasMal.length === AVDELNINGAR.length
              ? 'Inga mål satta ännu — utan mål finns ingen lönebudget, och därmed inget tak att jämföra planerade timmar mot. '
              : `Mål saknas för ${saknasMal.map(a => a.namn).join(', ')} — de raderna får inget tak. `}
            {isAdmin ? (
              <button onClick={() => setMalOppen(true)}
                style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: FARG,
                         fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                Sätt målen
              </button>
            ) : 'En administratör sätter dem.'}
          </p>
        )}
      </div>

      {/* ── MÅL OCH NYCKLAR ── */}
      <div className="card">
        <div onClick={() => setMalOppen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: FARG }}>
            <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: malOppen ? 'rotate(90deg)' : 'none', marginRight: 6 }}>▸</span>
            Mål och nycklar
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: 1 }}>
            {AVDELNINGAR.filter(a => mal[a.id] != null).length} av {AVDELNINGAR.length} avdelningar har mål
            {status === 'sparat' && <span style={{ color: '#3B6D11', fontWeight: 600 }}> · sparat</span>}
            {status === 'fel' && <span style={{ color: '#A32D2D', fontWeight: 600 }}> · kunde inte spara</span>}
          </span>
        </div>

        {malOppen && (
          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            <div style={{ minWidth: 640 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', gap: 8, fontSize: 11,
                            fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase',
                            letterSpacing: '0.05em', paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
                <span>Avdelning</span>
                <span style={{ textAlign: 'right' }}>Ifjol denna period</span>
                <span style={{ textAlign: 'right' }}>Mål-personal%</span>
                <span style={{ textAlign: 'right' }}>Rörlig kr/h</span>
                <span style={{ textAlign: 'right' }}>Fast lön/vecka</span>
              </div>
              {AVDELNINGAR.map(avd => {
                const n = nycklar[avd.id];
                const ifjolPct = niva?.[avd.id]?.pct;
                const forslag = malForslag(ifjolPct);
                return (
                  <div key={avd.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', gap: 8,
                                             padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 13, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: avd.farg }}>{avd.namn}</span>
                    <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {ifjolPct != null ? `${Math.round(ifjolPct * 100)}%` : '–'}
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      {isAdmin ? (
                        <input
                          type="number" min="5" max="200" step="1"
                          value={mal[avd.id] != null ? Math.round(mal[avd.id] * 100) : ''}
                          placeholder={forslag != null ? String(Math.round(forslag * 100)) : '–'}
                          onChange={(e) => sattMal(avd.id, e.target.value === '' ? null : Number(e.target.value))}
                          style={{ width: 62, textAlign: 'right', border: '1px solid var(--border)', borderRadius: 6,
                                   padding: '3px 6px', fontSize: 13, fontFamily: 'system-ui,sans-serif',
                                   background: 'transparent', color: 'var(--text)' }}
                        />
                      ) : (
                        <span>{mal[avd.id] != null ? `${Math.round(mal[avd.id] * 100)}%` : '–'}</span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 4 }}>%</span>
                    </span>
                    <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {n.krPerTim ? `${fmt(n.krPerTim)} kr` : '–'}
                    </span>
                    <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {n.fastPerDag > 0 ? `${fmt(n.fastPerDag * 7)} kr` : '–'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="note" style={{ marginTop: 10 }}>
              <strong>Ifjol denna period</strong> = vad fjolårets faktiska bemanning hade kostat med dagens
              lönenivå, som andel av fjolårets intäkt. Det är utgångsläget att förbättra — inte ett mål.
              Grå siffra i målrutan är förslaget (ifjol minus 5 procentenheter).
              <strong> Rörlig kr/h</strong> är stämplad lön ÷ arbetade timmar de senaste 90 dagarna
              (365 när säsongen gjort underlaget för tunt). <strong>Fast lön</strong> är styrfilens
              månadslöner — de går inte att schemalägga bort och dras därför av före timtaket.
              {!isAdmin && ' Målen ändras av en administratör.'}
            </div>
          </div>
        )}
      </div>

      {/* ── VECKORNA ── */}
      {rader.map((v) => {
        const overP = v.summa.avvikelse != null && v.summa.avvikelse > 0;
        return (
          <div className="card" key={v.nyckel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                v.{v.veckoNr} · {dagMan(v.from)}–{dagMan(v.till)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {v.gastIfjol > 0 || v.gastNu > 0 ? (
                  <>
                    Bokningsläge: <strong style={{ color: v.pace >= 1 ? '#3B6D11' : '#A32D2D' }}>
                      {v.pace.toFixed(2).replace('.', ',')}×
                    </strong>
                    {' '}({v.gastNu} gästnätter mot {v.gastIfjol} ifjol)
                    {v.tunnt && ' · för tunt underlag, prognos = ifjol rakt av'}
                    {v.kapad && ' · kapad'}
                  </>
                ) : 'Inget bokningsunderlag'}
              </div>
            </div>

            <div style={{ overflowX: 'auto', marginTop: 10 }}>
              <div style={{ minWidth: 660 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr 1fr', gap: 8, fontSize: 11,
                              fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase',
                              letterSpacing: '0.05em', paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
                  <span>Avdelning</span>
                  <span style={{ textAlign: 'right' }}>Prognos</span>
                  <span style={{ textAlign: 'right' }}>Tillåtna h</span>
                  <span style={{ textAlign: 'right' }}>Planerat</span>
                  <span style={{ textAlign: 'right' }}>Avvikelse</span>
                  <span style={{ textAlign: 'right' }}>Ifjol h</span>
                </div>

                {v.rader.map(r => {
                  const over = r.avvikelse != null && r.avvikelse > 0;
                  return (
                    <div key={r.avd.id} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr 1fr', gap: 8,
                                                 padding: '9px 0', borderBottom: '1px solid var(--border)',
                                                 fontSize: 13, alignItems: 'center',
                                                 opacity: r.stangt ? 0.45 : 1 }}>
                      <span style={{ fontWeight: 600, color: r.avd.farg }}>
                        {r.avd.namn}
                        {r.avd.pace && <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 5 }}>gästdriven</span>}
                      </span>
                      <span style={{ textAlign: 'right' }}>{r.stangt ? 'stängt ifjol' : tkr(r.prognos)}</span>
                      <span style={{ textAlign: 'right', fontWeight: 700 }}>
                        {r.fastAterUpp
                          ? <span title="Fasta löner är större än hela lönebudgeten" style={{ color: '#A32D2D', fontWeight: 600, fontSize: 12 }}>fast äter upp</span>
                          : h1(r.tillatna)}
                      </span>
                      <span style={{ textAlign: 'right' }}>
                        {isAdmin ? (
                          <input
                            type="number" min="0" step="1" placeholder="–"
                            value={r.planTim != null ? r.planTim : ''}
                            onChange={(e) => sattPlan(v.nyckel, r.avd.id, e.target.value === '' ? null : Number(e.target.value))}
                            style={{ width: 62, textAlign: 'right', border: '1px solid var(--border)', borderRadius: 6,
                                     padding: '3px 6px', fontSize: 13, fontFamily: 'system-ui,sans-serif',
                                     background: 'transparent', color: 'var(--text)' }}
                          />
                        ) : h1(r.planTim)}
                      </span>
                      <span style={{ textAlign: 'right', fontWeight: 700, color: r.avvikelse == null ? 'var(--text-secondary)' : over ? '#A32D2D' : '#3B6D11' }}>
                        {r.avvikelse == null ? '–' : `${r.avvikelse > 0 ? '+' : ''}${h1(r.avvikelse)}`}
                      </span>
                      <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{h1(r.ifjolTim)}</span>
                    </div>
                  );
                })}

                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr 1fr', gap: 8,
                              padding: '10px 0 2px', fontSize: 13, fontWeight: 700, alignItems: 'center' }}>
                  <span>Summa</span>
                  <span style={{ textAlign: 'right' }}>{tkr(v.summa.prognos)}</span>
                  <span style={{ textAlign: 'right' }}>{h1(v.summa.tillatna)}</span>
                  <span style={{ textAlign: 'right' }}>{h1(v.summa.planTim)}</span>
                  {/* Färg bara när det FINNS ett tak att jämföra mot — grönt på
                      en oberäknad avvikelse läser som "allt bra" och ljuger. */}
                  <span style={{ textAlign: 'right', color: v.summa.avvikelse == null ? 'var(--text-secondary)' : overP ? '#A32D2D' : '#3B6D11' }}>
                    {v.summa.avvikelse == null ? '–'
                      : `${v.summa.avvikelse > 0 ? '+' : ''}${h1(v.summa.avvikelse)}`}
                  </span>
                  <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{h1(v.summa.ifjolTim)}</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <ExportBtn name={`bemanning-v${v.veckoNr}`} />
            </div>
          </div>
        );
      })}

      <div className="card">
        <div className="note">
          Källa: Qlik · intäkt och lön per dag och resultatenhet, arbetade timmar via Personalkollen,
          bokningsläge ur Visbook. <strong>Prognos</strong> = fjolårets samma veckodag (−364 dgr) ×
          bokningsläget för veckan. Bokningsläget räknas på <strong>bekräftade</strong> gästnätter —
          obekräftade offerter är uppsida, inte något att bemanna för — och slår bara på de gästdrivna
          avdelningarna. Hotellrestaurangen följer huset, Brasserie och Knallen har egna gäster.
          Faktorn kapas vid 0,3× och 3,5×, och sätts till 1 när fjolåret hade under 20 gästnätter.
          <strong> Planerat</strong> matas in för hand — Personalkollens framtida scheman finns inte i Qlik,
          datan slutar på dagens datum.
        </div>
      </div>
    </div>
  );
}
